/* =========================================================================
   Boot.
   ========================================================================= */

import './styles.css'

import { initDelegation, toast, closeModal, isModalOpen, $ } from './ui/dom.js'
import { loadStore } from './data/store.js'
import { requestPersistence } from './data/db.js'
import { render, showList, setBanner, isDismissed, state } from './app.js'

// Imported for their side effect: each module registers its own actions.
import './ui/imageEditor.js'

import { openRecipeForm } from './ui/form.js'
import { mergedShoppingList } from './ui/shopping.js'
import { openIO } from './ui/io.js'

/* ---------------- service worker ---------------- */

if ('serviceWorker' in navigator) {
  import('virtual:pwa-register')
    .then(({ registerSW }) => {
      registerSW({
        immediate: true,
        onOfflineReady() { console.info('[app] ready to work offline') },
      })
    })
    .catch(() => { /* plugin unavailable — the app still works, just not offline */ })
}

/* ---------------- header wiring ---------------- */

function togglePlan() {
  document.body.classList.toggle('planmode')
  if (!document.body.classList.contains('planmode')) state.selected.clear()
  if (state.view !== 'list') showList(); else render()
}

function wireHeader() {
  $('homeBtn').addEventListener('click', showList)
  $('addBtn').addEventListener('click', () => openRecipeForm(null))
  $('ioBtn').addEventListener('click', openIO)
  $('planBtn').addEventListener('click', togglePlan)
  $('planCancelBtn').addEventListener('click', togglePlan)
  $('planListBtn').addEventListener('click', mergedShoppingList)

  // Close the modal by tapping the backdrop, or with Escape.
  $('overlay').addEventListener('click', ev => {
    if (ev.target.id === 'overlay') closeModal()
  })
  document.addEventListener('keydown', ev => {
    if (ev.key === 'Escape' && isModalOpen()) closeModal()
  })
}

/* ---------------- install hint ---------------- */

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * iOS has no beforeinstallprompt event — Safari only installs via the
 * Share sheet, so the best we can do is tell the user how.
 *
 * This matters beyond convenience: an installed PWA gets much more durable
 * storage than a Safari tab, which is cleared after ~7 days of no visits.
 */
function maybeShowInstallHint() {
  if (isStandalone()) return
  if (!isIOS()) return
  if (isDismissed('installHint')) return
  setBanner(
    '<b>Add this to your Home Screen.</b> Tap the Share button in Safari, then ' +
    '“Add to Home Screen”. It opens full-screen, works offline, and — importantly — ' +
    'stops Safari clearing your recipes after a week of not opening the app.',
    'installHint'
  )
}

/* ---------------- go ---------------- */

async function boot() {
  initDelegation()
  wireHeader()

  try {
    const res = await loadStore()
    render()
    if (res.source === 'migrated') {
      toast(`Brought ${res.count} recipes across from the old version`)
    }
  } catch (e) {
    console.error(e)
    $('main').innerHTML =
      '<div class="empty">Couldn\'t open the recipe database.<br><br>' +
      'If you are browsing privately, Safari blocks storage — open the app in a normal tab.</div>'
    return
  }

  await requestPersistence()
  maybeShowInstallHint()
}

boot()
